import { HttpClient, provideHttpClient } from '@angular/common/http'
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core'
import { provideAnimations } from '@angular/platform-browser/animations'
import { provideRouter, withHashLocation } from '@angular/router'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { KeycloakService } from 'keycloak-angular'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { firstValueFrom } from 'rxjs'
import { routes } from './app.routes'
import { ConfigService } from './shared/services/config.service'
import { stageReducer } from './shared/store/stage/stage.reducer'
import { SimpleNotificationsModule } from 'angular2-notifications'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { providePrimeNG } from 'primeng/config'
import Aura from '@primeng/themes/aura'

export const appConfig: ApplicationConfig = {
   providers: [
      provideAnimationsAsync(),
      providePrimeNG({
         theme: {
            preset: Aura,
            options: {
               darkModeSelector: false || 'none',
               cssLayer: {
                  name: 'primeng',
                  order: 'tailwind-base, tailwind-utilities, primeng',
               },
            },
         },
      }),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideHttpClient(),
      provideAnimations(),
      provideRouter(routes, withHashLocation()),
      {
         provide: DynamicDialogRef,
         useValue: [],
      },
      TranslateModule.forRoot({
         defaultLanguage: 'en-US',
         useDefaultLang: true,
         loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
         },
      }).providers!,
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [TranslateService],
         useFactory: initializeTranslation,
      },
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [ConfigService],
         useFactory: (configService: ConfigService) => {
            return () => configService.initRuntimeConfig()
         },
      },
      {
         provide: APP_INITIALIZER,
         useFactory: initializeKeycloak,
         multi: true,
         deps: [KeycloakService, HttpClient],
      },
      KeycloakService,
      DialogService,
      importProvidersFrom(
         StoreModule.forRoot({ stage: stageReducer }),
         StoreDevtoolsModule.instrument({
            maxAge: 25,
         }),
         SimpleNotificationsModule.forRoot({
            timeOut: 5000,
            showProgressBar: true,
            pauseOnHover: true,
            clickToClose: true,
            position: ['bottom', 'right'],
            lastOnBottom: true,
            maxStack: 3,
            maxLength: 500,
            theClass: 'app-notify',
            preventDuplicates: true,
         })
      ),
   ],
}

// required for AoT
export function HttpLoaderFactory(http: HttpClient) {
   return new TranslateHttpLoader(http)
}

function initializeTranslation(translate: TranslateService) {
   return () => {
      translate.setDefaultLang('en-US')
      const browserLang = navigator.language
      // Usa en-US se la lingua del browser non è supportata
      const langToUse = browserLang === 'en-US' ? browserLang : 'en-US'
      return firstValueFrom(translate.use(langToUse))
   }
}

function initializeKeycloak(keycloak: KeycloakService, httpClient: HttpClient) {
   return () => {
      return firstValueFrom(httpClient.get<RuntimeConfig>(`assets/configs/properties.json?ts=${Date.now()}`)).then((dc) => {
         return keycloak.init({
            config: {
               url: dc.keycloak.authUrl,
               realm: dc.keycloak.realm,
               clientId: dc.keycloak.clientId,
            },
            initOptions: { enableLogging: true, checkLoginIframe: false, flow: 'standard', onLoad: 'check-sso' },
         })
      })
   }
}

interface RuntimeConfig {
   keycloak: {
      authUrl: string
      clientId: string
      realm: string
   }
}
